import React, { useState, useEffect } from "react";
import MyModal from "components/MyModal";
import * as yup from "yup";
import {
  indetityApiAppToken,
} from "redux/services/identity";
import {
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Select,
  Box,
} from "@chakra-ui/react";
import Alert from "components/Alert";
import { useDispatch } from "react-redux";
import {
  useGetOrganizationQuery,
  useGetProfileQuery,
} from "redux/services/membership";
import {
  PaymentMethod,
  paymentMethodOptions,
  Roles,
} from "common";
import { getOrganisationMembership } from "utils";
import { getAlertTitle } from "containers/CreateEnterpriseUser/CreateEnterpriseUser.helper";
import { useCreateEnterpriseUserService } from "containers/CreateEnterpriseUser/CreateEnterpriseUser.service";
import LoadingOverlay from "components/LoadingOverlay";

interface CreateEnterpriseUserProps {
  isOpen: boolean;
  setOpenModalCreate: () => void;
}

const schema = yup.object().shape({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  emailAddress: yup
    .string()
    .email("Invalid email")
    .required("Email is required"),
  // .max(8, 'The length must be between 0 and 8')
  bussinessUnitName: yup
    .string()
    .matches(/^[a-zA-Z0-9\s]*$/, "Only alphabets, numbers and space are allowed")
    .required("Business Unit Name is required"),
  bussinessUnitCode: yup
    .string()
    .min(2, "Business Unit Code must be at least 2 characters long")
    .max(15, "Business Unit Code must be at less than 12 characters long")
    .matches(/^[A-Z0-9]+$/, "Only uppercase alphabets and numbers without space are allowed")
    .required("Business Unit Code is required"),
  userRole: yup.string().required("User Role is required"),
});

function CreateEnterpriseUser(props: CreateEnterpriseUserProps) {
  const { isOpen, setOpenModalCreate } = props;

  const { handleSubmit, isLoading, error, isFetchingOrgData } = useCreateEnterpriseUserService(setOpenModalCreate);
  const dispatch = useDispatch();

  const { data: profileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );
  const isPaymentOps: boolean = profileData?.data?.listRoles?.includes(Roles.PaymentOps) || false;

  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    emailAddress: string;
    bussinessUnitName: string;
    bussinessUnitCode: string;
    paymentMethod: string;
    userRole?: string;
    serviceProviderCode?: string;
  }>({
    firstName: "",
    lastName: "",
    emailAddress: "",
    bussinessUnitName: "",
    bussinessUnitCode: "",
    paymentMethod: PaymentMethod.NORMAL_PAY,
    userRole: isPaymentOps ? Roles.BusinessAdmin : Roles.BusinessApprover,
    serviceProviderCode: "",
  });

  const [isFormValid, setIsFormValid] = useState(false);

  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    bussinessUnitName?: string;
    bussinessUnitCode?: string;
    paymentMethod?: string;
    serviceProviderCode?: string;
  }>({});

  const { data: organizationData } = useGetOrganizationQuery(
    {
      orgId: membershipOrganisation?.organisationId || "",
      userId: profileData?.data?.userId || "",
    },
    { skip: !membershipOrganisation?.organisationId || !profileData || isPaymentOps }
  );

  useEffect(() => {
    return () => {
      dispatch(indetityApiAppToken.util.resetApiState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpen && organizationData && !isPaymentOps) {
      setFormData({
        firstName: "",
        lastName: "",
        emailAddress: "",
        bussinessUnitName: organizationData?.data?.companyName,
        bussinessUnitCode: organizationData?.data?.leiNumber,
        serviceProviderCode: organizationData?.data?.companyNumber,
        paymentMethod:
          organizationData?.data?.tradingName || PaymentMethod.NORMAL_PAY,
        userRole: Roles.BusinessOwner,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, organizationData]);

  const handleChange = async (e: any, useTrim?: boolean) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: useTrim ? value.trim() : value,
    }));

    if (name === "bussinessUnitCode") {
      try {
        await schema.validateAt("bussinessUnitCode", {
          ...formData,
          bussinessUnitCode: value,
        })
        setErrors(prev => ({
          ...prev,
          bussinessUnitCode: undefined,
        }))
      } catch (err: any) {
        if (err instanceof yup.ValidationError) {
          setErrors(prev => ({
            ...prev,
            bussinessUnitCode: err.message,
          }))
        }
      }
    }
  };

  useEffect(() => {
    const validate = async () => {
      try {
        await schema.validate(formData, { abortEarly: false });
        setIsFormValid(true);
      } catch {
        setIsFormValid(false);
      }
    };

    validate();
  }, [formData]);

  return (
    <MyModal
      isOpen={isOpen}
      onClose={() => setOpenModalCreate()}
      title={isPaymentOps ? "Register Business Administrator" : "Register Enterprise User"}
      submitBtnText="Register"
      cancelBtnText="Cancel"
      onSubmit={() => handleSubmit(formData, schema)}
      loadingSubmit={
        isLoading
      }
      scrollBehavior="inside"
      disabledCloseOutSide
      disableSubmit={!isFormValid}
    >
      {error && (
        <Alert
          data-testid="alert-error"
          status="error"
          mb="10px"
          title={getAlertTitle((error) as any)}
        />
      )}
      <Box
        position="relative"
        m="0"
        p="0"
        width="100%"
        height="100%"
      >
        <Box position="absolute"
          top="0"
          left="0"
          w="100%"
          h="100%"
          backdropBlur={"2px"}
          zIndex={1}
          backgroundColor={"whiteAlpha.800"}
          display={isFetchingOrgData ? "flex" : "none"}
          justifyContent="center"
          alignItems="center"
        >
          <LoadingOverlay height="20px" />
        </Box>
        <FormControl
          mb="12px"
          isInvalid={!!errors.emailAddress}
        >
          <FormLabel>Email Address</FormLabel>
          <Input
            value={formData.emailAddress}
            name="emailAddress"
            data-testid="email-create-enterprise"
            isRequired
            onChange={handleChange}
            disabled={isLoading}
          />
          {errors.emailAddress && (
            <FormErrorMessage fontSize={12}>
              {errors.emailAddress}
            </FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.firstName}>
          <FormLabel>First Name</FormLabel>
          <Input
            value={formData.firstName}
            name="firstName"
            data-testid="firstname-create-enterprise"
            isRequired
            onChange={handleChange}
            disabled={isLoading}
          />
          {errors.firstName && (
            <FormErrorMessage fontSize={12}>{errors.firstName}</FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.lastName}>
          <FormLabel>Last Name</FormLabel>
          <Input
            value={formData.lastName}
            name="lastName"
            data-testid="lastname-create-enterprise"
            isRequired
            onChange={handleChange}
            disabled={isLoading}
          />
          {errors.lastName && (
            <FormErrorMessage fontSize={12}>{errors.lastName}</FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.bussinessUnitName}>
          <FormLabel>Business Unit Name</FormLabel>
          <Input
            value={formData.bussinessUnitName}
            name="bussinessUnitName"
            data-testid="bussinessUnitName-create-enterprise"
            isRequired
            onChange={handleChange}
            disabled={!isPaymentOps || isLoading}
          />
          {errors.bussinessUnitName && (
            <FormErrorMessage fontSize={12}>
              {errors.bussinessUnitName}
            </FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.serviceProviderCode}>
          <FormLabel>Service Provider Code</FormLabel>
          <Input
            value={formData.serviceProviderCode}
            name="serviceProviderCode"
            data-testid="serviceProvideCode-create-enterprise"
            isRequired
            onChange={(e) => handleChange(e, true)}
            maxLength={15}
            disabled={!isPaymentOps || isLoading}
          />
          {errors.serviceProviderCode && (
            <FormErrorMessage fontSize={12}>
              {errors.serviceProviderCode}
            </FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.bussinessUnitCode}>
          <FormLabel>Business Unit Code</FormLabel>
          <Input
            value={formData.bussinessUnitCode}
            name="bussinessUnitCode"
            data-testid="bussinessUnitCode-create-enterprise"
            isRequired
            onChange={(e) => handleChange(e, true)}
            maxLength={15}
            disabled={!isPaymentOps || isLoading}
          />
          {errors.bussinessUnitCode && (
            <FormErrorMessage fontSize={12}>
              {errors.bussinessUnitCode}
            </FormErrorMessage>
          )}
        </FormControl>
        <FormControl mb="12px" isInvalid={!!errors.paymentMethod}>
          <FormLabel>Payment Method</FormLabel>
          <Select
            name="paymentMethod"
            data-testid="paymentMethod-create-enterprise"
            isRequired
            onChange={handleChange}
            value={formData.paymentMethod}
            disabled={!isPaymentOps || isLoading}
          >
            {paymentMethodOptions.map((option) => (
              <option value={option.value}>{option.label}</option>
            ))}
          </Select>
        </FormControl>
        {!isPaymentOps && (
          <FormControl mb="12px">
            <FormLabel>User Role</FormLabel>
            <Select
              name="userRole"
              data-testid="userRole-create-enterprise"
              isRequired
              onChange={handleChange}
              value={formData.userRole}
              disabled={isLoading}
            >
              <option value={Roles.BusinessApprover}>Business Checker</option>
              <option value={Roles.BusinessOwner}>Business Maker</option>
              <option value={Roles.BUApprover}>Business Approver</option>
            </Select>
          </FormControl>
        )}
      </Box>

    </MyModal >
  );
}

export default CreateEnterpriseUser;
